import { useState, useEffect } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { dataService } from '../../functions/apis/dataService';
import type { StackedBarData } from '../../components/charts/StackedBarChart';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseIncomeDate(dateStr: string) {
  if (dateStr.length === 6) {
    return { year: dateStr.substring(0, 4), month: parseInt(dateStr.substring(4, 6), 10) };
  }
  if (dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    return { year, month: parseInt(month, 10) };
  }
  return null;
}

export function useUnitIncomeChart() {
  const { selectedClient, selectedUnit } = useFilter();
  const [incomeYear, setIncomeYear] = useState('');
  const [yearOptions, setYearOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [stackedBarData, setStackedBarData] = useState<{ xLabels: string[]; stacks: StackedBarData[] }>({
    xLabels: [],
    stacks: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
      setStackedBarData({ xLabels: [], stacks: [] });
      setYearOptions([]);
      return;
    }

    const fetchIncomeData = async () => {
      setLoading(true);
      try {
        const incomeData = await dataService.getIncomeStatement({
          user_id_no: selectedClient.user_id_no,
          unit_id_no: selectedUnit.unit_id_no!,
        });

        if (!incomeData?.length) {
          setStackedBarData({ xLabels: [], stacks: [] });
          setYearOptions([]);
          return;
        }

        const yearSet = new Set<string>();
        incomeData.forEach((item) => {
          const parsed = parseIncomeDate(item.calendar_date);
          if (parsed) yearSet.add(parsed.year);
        });

        const sortedYears = Array.from(yearSet).sort((a, b) => b.localeCompare(a));
        setYearOptions(sortedYears.map((y) => ({ id: y, label: y })));

        let yearToUse = incomeYear;
        if (!yearToUse || !yearSet.has(yearToUse)) {
          yearToUse = sortedYears[0];
          setIncomeYear(yearToUse);
        }

        const profitByMonth = new Array(12).fill(0);
        const lossByMonth = new Array(12).fill(0);

        incomeData.forEach((item) => {
          if (item.category_code !== 4 && item.category_code !== 24) return;
          const parsed = parseIncomeDate(item.calendar_date);
          if (!parsed || parsed.year !== yearToUse) return;
          const value = item.category_value ?? 0;
          const monthIndex = parsed.month - 1;
          if (value > 0) profitByMonth[monthIndex] += value;
          else if (value < 0) lossByMonth[monthIndex] += value;
        });

        setStackedBarData({
          xLabels: MONTH_LABELS,
          stacks: [
            { label: 'Profit', values: profitByMonth, color: 'success' },
            { label: 'Loss', values: lossByMonth, color: 'error' },
          ],
        });
      } catch (error) {
        console.error('Error fetching income statement data:', error);
        setStackedBarData({ xLabels: [], stacks: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchIncomeData();
  }, [selectedClient, selectedUnit, incomeYear]);

  return {
    incomeYear,
    yearOptions,
    stackedBarData,
    loading,
    setIncomeYear,
  };
}
